import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchFinancialDocument } from "@lib/financialDocument";
import { StandardModal } from "@design-system/Modal/Modal";
import { Loader } from "@design-system/Loader/Loader";
import { financialDocumentQueryKeys } from "../lib";
import { useMaximizedModal } from "../useMaximizedModal";
import { FinancialDocumentDetails } from "./FinancialDocumentDetails";
import { FinancialDocumentDetailsView } from "./usePathBasedFinancialDocumentDetailsView";
import { financialDocumentDetailsContext } from "./financialDocumentDetails.context";

interface Props {
    financialDocumentId: number;
    open: boolean;
    onClose: () => void;
}

/**
 * Standalone financial document details modal, functionally equivalent to
 * the modal shown from the Documents page (preview + matches/details), but
 * driven by local state instead of URL route params so it can be opened as
 * an overlay from anywhere (e.g. a chat message document reference chip)
 * without navigating away from the current page. Single-document only — no
 * prev/next navigation between documents.
 */
export const FinancialDocumentDetailsModal: React.FC<Props> = ({
    financialDocumentId,
    open,
    onClose,
}) => {
    const { isMaximized, toggleMaximized } =
        useMaximizedModal(financialDocumentId);
    const [currentView, setCurrentView] = useState(
        FinancialDocumentDetailsView.DEFAULT,
    );

    const { data: financialDocument } = useQuery({
        queryKey: financialDocumentQueryKeys.detail(financialDocumentId),
        queryFn: () => fetchFinancialDocument(financialDocumentId),
    });

    const changeView = useCallback((view?: FinancialDocumentDetailsView) => {
        setCurrentView(view ?? FinancialDocumentDetailsView.DEFAULT);
    }, []);

    const [hasShown, setHasShown] = useState(false);
    useEffect(() => {
        if (open) {
            setHasShown(true);
        }
    }, [open]);

    const contextValue = useMemo(
        () => ({
            currentView,
            changeView,
            isMaximized,
            toggleMaximized,
            shouldShowNavigation: false,
            canGoToPrevious: false,
            canGoToNext: false,
            goToPrevious: () => {},
            goToNext: () => {},
        }),
        [currentView, changeView, isMaximized, toggleMaximized],
    );

    if (!hasShown) {
        return null;
    }

    return (
        <StandardModal
            className="fullscreen-modal"
            show={open}
            onHide={onClose}
            fullscreen={isMaximized}
        >
            {financialDocument ? (
                <financialDocumentDetailsContext.Provider value={contextValue}>
                    <FinancialDocumentDetails
                        financialDocument={financialDocument}
                    />
                </financialDocumentDetailsContext.Provider>
            ) : (
                <Loader />
            )}
        </StandardModal>
    );
};
